import FullscreenLoadingIndicator from "@/features/Common/components/FullscreenLoadingIndicator";
import { useEffect, useState } from "react";
import { registerEvents } from "@unu/web-native-bridge";
import { BridgeEvent } from "@unu/ui-enums";
import { convertUpperSnakeToCamelCase } from "@/utils";
import FaceScanComplete from "../BinahScanFlow/components/FaceScanComplete";
import GenericError from "../BinahScanFlow/components/GenericError";
import PermissionIssuesError from "../BinahScanFlow/components/PermissionIssuesError";
import NoCameraFoundError from "../BinahScanFlow/components/NoCameraFoundError";
import { HealthMonitorCodes } from "@biosensesignal/web-sdk";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "@/services/analytics-adapter";
import { submitFaceScanMeasurements } from "@/services/core-api-adapter";
import DefaultError from "@/features/Common/components/DefaultError";
import { Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

enum ErrorMap {
  GENERIC = "GENERIC",
  DEVICE = "DEVICE",
  PERMISSIONS_ISSUE = "PERMISSIONS_ISSUE",
  NO_CAMERA_FOUND = "NO_CAMERA_FOUND",
  BATTERY_TOO_LOW = "BATTERY_TOO_LOW",
}

interface FaceScanError {
  showError: boolean;
  errorType: string;
  errorCode: string;
}

interface FaceScanErrorProps {
  error: FaceScanError;
  onComeBackLater: () => void;
  onTryAgain: () => void;
}

function FaceScanError({
  error,
  onComeBackLater,
  onTryAgain,
}: FaceScanErrorProps) {
  switch (error.errorType) {
    case ErrorMap.GENERIC:
      return (
        <GenericError
          onComeBackLater={onComeBackLater}
          onTryAgain={onTryAgain}
          errorCode={error.errorCode as unknown as HealthMonitorCodes}
        />
      );
    case ErrorMap.PERMISSIONS_ISSUE:
      return (
        <PermissionIssuesError
          onComeBackLater={onComeBackLater}
          onTryAgain={onTryAgain}
        />
      );
    case ErrorMap.NO_CAMERA_FOUND:
      return (
        <NoCameraFoundError
          onComeBackLater={onComeBackLater}
          onTryAgain={onTryAgain}
        />
      );
  }

  return (
    <GenericError
      onComeBackLater={onComeBackLater}
      onTryAgain={onTryAgain}
      errorCode={error.errorCode as unknown as HealthMonitorCodes}
    />
  );
}

export default function BinahScanFlowNative({ onCompletedFlow }: any) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isSubmittingVitals, setIsSubmittingVitals] = useState<boolean>(false);
  const [isSubmittingError, setIsSubmittingError] = useState<boolean>(false);
  const [faceScanResultsFromNative, setFaceScanResultsFromNative] =
    useState<any>(null);
  const [finalScanResults, setFinalScanResults] = useState(null);
  const [faceScanError, setFaceScanError] = useState<{
    error: { code: string };
  } | null>(null);

  useEffect(() => {
    if (
      faceScanResultsFromNative !== null &&
      faceScanResultsFromNative.isError === false &&
      faceScanResultsFromNative?.results !== null
    ) {
      if (faceScanResultsFromNative?.results.length === 0) {
        onCompletedFlow();
      }

      const transformedFinalVitals = faceScanResultsFromNative.results.reduce(
        (acc: any, vitalSign: any) => {
          const transformedType = convertUpperSnakeToCamelCase(vitalSign.type);
          acc[transformedType] = { ...vitalSign };
          return acc;
        },
        {}
      );

      setIsSubmittingVitals(true);
      setIsSubmittingError(false);
      submitFaceScanMeasurements({
        vitalSigns: transformedFinalVitals,
        healthCheckQuestionnaireData: null,
      })
        .then(() => {
          setIsSubmittingVitals(false);
          setIsSubmittingError(false);
        })
        .catch(() => {
          setIsSubmittingVitals(false);
          setIsSubmittingError(true);
        });

      setFinalScanResults(transformedFinalVitals);
    } else if (
      faceScanResultsFromNative !== null &&
      faceScanResultsFromNative.isError === true
    ) {
      trackEvent({
        event: "action.healthCheckFaceScanErrorEncountered",
        source: "health check",
        errorCode: faceScanResultsFromNative.error.code,
      });
      setFaceScanError(faceScanResultsFromNative);
    }
  }, [faceScanResultsFromNative, onCompletedFlow]);

  useEffect(() => {
    const events = {
      [BridgeEvent.ReceiveData]: (data: any) =>
        setFaceScanResultsFromNative(data),
    };

    return registerEvents(events);
  }, []);

  function onTryAgain() {
    navigate("/binah-scan?skipScanPrompt=true");
  }

  function getErrorType(errorCode: string) {
    switch (Number(errorCode)) {
      case 1001:
        return ErrorMap.NO_CAMERA_FOUND;
      case 1005:
        return ErrorMap.PERMISSIONS_ISSUE;
      default:
        return ErrorMap.GENERIC;
    }
  }

  return (
    <>
      {finalScanResults !== null && faceScanError === null && (
        <FaceScanComplete
          scanResults={finalScanResults}
          onDone={onCompletedFlow}
        />
      )}

      {faceScanError !== null && (
        <>
          <FaceScanError
            error={{
              errorCode: faceScanError.error.code,
              errorType: getErrorType(faceScanError.error.code),
              showError: true,
            }}
            onComeBackLater={onCompletedFlow}
            onTryAgain={onTryAgain}
          />
        </>
      )}

      {((faceScanError == null && finalScanResults === null) ||
        isSubmittingVitals === true) && <FullscreenLoadingIndicator />}

      {isSubmittingError && !isSubmittingVitals && (
        <DefaultError>
          <Typography variant="h2">{t("GenericError.title")}</Typography>
          <Typography pb={2}>{t("GenericError.content")}</Typography>
          <Stack direction="row" spacing={2}>
            <Button
              fullWidth
              onClick={() => navigate("/home")}
              variant="contained"
            >
              {t("common.comeBackButton")}
            </Button>
          </Stack>
        </DefaultError>
      )}
    </>
  );
}
